import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { BookOpen, Video, ArrowRight, Star, Users, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export default function Resources() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [eventsData, setEventsData] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const { toast } = useToast();

  // Fetch events data from Google Sheets
  useEffect(() => {
    const fetchEventsData = async () => {
      try {
        setEventsLoading(true);
        const response = await api.getEventsData();
        if (response.ok && response.data && response.data.events) {
          setEventsData(response.data.events);
        }
      } catch (error) {
        console.error('Error fetching events data:', error);
        // Keep fallback data if API fails
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEventsData();
  }, []);

  async function handleSubscribe(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.newsletter({ 
        email: newsletterEmail, 
        contact_source: 'resources_page',
        consent_timestamp: new Date().toISOString() 
      });
      toast({
        title: "Subscribed!",
        description: "Thanks! You'll receive our latest financial insights.",
        variant: "success"
      });
      setNewsletterEmail('');
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || 'Subscription failed. Please try again.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  }
  const resources = [
    {
      icon: <BookOpen className="h-8 w-8" />,
      title: "Educational Guides",
      description: "Download comprehensive guides on wealth building, financial planning, and investment strategies.",
      features: ["Wealth Building Guide", "Retirement Planning", "Tax Strategies", "Investment Basics"],
      action: "View Guides",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <Video className="h-8 w-8" />,
      title: "Educational Webinars",
      description: "Join our expert-led webinars covering essential financial topics and advanced strategies.",
      features: ["Monthly Webinars", "Q&A Sessions", "Expert Interviews", "Strategy Workshops"],
      action: "Register Now",
      color: "from-purple-500 to-purple-600"
    }
  ];

  // Fallback events data (used if API fails or while loading)
  const fallbackEvents = [
    {
      event_name: "Retirement Planning Masterclass",
      event_date: "18 Oct, 2025",
      event_time: "01:00 PM CT",
      people_registered: "100+ registered"
    },
    {
      event_name: "Tax Strategy Workshop",
      event_date: "25 Oct, 2025",
      event_time: "01:00 PM CT",
      people_registered: "200+ registered"
    },
    {
      event_name: "Wealth Building Fundamentals",
      event_date: "1 Nov, 2025",
      event_time: "01:00 PM CT",
      people_registered: "300+ registered"
    }
  ];

  // Use fetched data or fallback
  const displayEvents = eventsData.length > 0 ? eventsData : fallbackEvents;

  return (
    <section id="resources" className="relative py-24 bg-[#0D1B2A] text-white overflow-hidden">
      {/* ── Background Lighting ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute -bottom-20 right-10 w-[500px] h-[500px] bg-[#3D6ACC]/15 rounded-full blur-[150px]" />
        <div 
          className="absolute inset-0 opacity-[0.05]" 
          style={{
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
            <BookOpen className="w-4 h-4 text-[#C9A84C]" />
            Knowledge & Masterclasses
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-playfair tracking-tight">
            Empower Yourself With <span className="text-gradient-gold">Financial Intelligence</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto font-opensans leading-relaxed">
            Access our masterclass workshops, educational guides, and tax-advantaged strategy blueprints designed to elevate your financial life.
          </p>
        </div>
        
        {/* Main Resources Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16 max-w-4xl mx-auto">
          {resources.map((resource, index) => (
            <div 
              key={index} 
              className="group bg-[#162236]/90 border border-[#C9A84C]/25 hover:border-[#C9A84C]/60 hover:-translate-y-2 transition-all duration-300 rounded-2xl p-8 shadow-2xl backdrop-blur-xl flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3.5 rounded-xl bg-gradient-to-br from-[#162236] to-[#0D1B2A] border border-[#C9A84C]/40 text-[#C9A84C] group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] transition-all duration-300">
                    {resource.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-playfair text-white mb-2 group-hover:text-[#E8D4A0] transition-colors duration-300">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      {resource.description}
                    </p>
                  </div>
                </div>

                <div className="space-y-2.5 mb-8 pt-2 border-t border-slate-700/60">
                  {resource.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2.5 text-xs sm:text-sm text-slate-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {resource.title === "Educational Guides" ? (
                <Link to="/blog" className="block">
                  <Button className="w-full h-12 text-sm font-bold bg-gradient-gold-cta text-[#070E1B] rounded-xl shadow-lg hover:opacity-95 transition-all duration-300 flex items-center justify-center gap-2">
                    {resource.action}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              ) : resource.title === "Educational Webinars" ? (
                <Link to="/webinar" className="block">
                  <Button className="w-full h-12 text-sm font-bold bg-gradient-gold-cta text-[#070E1B] rounded-xl shadow-lg hover:opacity-95 transition-all duration-300 flex items-center justify-center gap-2">
                    {resource.action}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <Button className="w-full h-12 text-sm font-bold bg-gradient-gold-cta text-[#070E1B] rounded-xl shadow-lg hover:opacity-95 transition-all duration-300 flex items-center justify-center gap-2">
                  {resource.action}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Upcoming Events & Newsletter */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Upcoming Events */}
          <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-8 shadow-2xl backdrop-blur-xl space-y-6">
            <div className="space-y-1 border-b border-slate-700/60 pb-4">
              <h3 className="text-xl font-bold font-playfair text-white flex items-center gap-2.5">
                <Video className="h-5 w-5 text-[#C9A84C]" />
                Upcoming Live Masterclasses
              </h3>
              <p className="text-xs text-slate-300">Reserve your virtual seat for interactive Q&A sessions</p>
            </div>

            <div className="space-y-4">
              {eventsLoading ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C] mx-auto" />
                  <p className="text-xs text-slate-400 mt-2">Loading upcoming schedule...</p>
                </div>
              ) : (
                displayEvents.map((event, index) => (
                  <div key={event.id || index} className="p-4 bg-[#0D1B2A] border border-[#C9A84C]/20 rounded-xl hover:border-[#C9A84C]/50 transition-all duration-300">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold text-base text-white">{event.event_name}</h4>
                      <span className="px-2.5 py-0.5 rounded-full bg-[#C9A84C]/20 text-[#E8D4A0] border border-[#C9A84C]/40 text-[11px] font-semibold">
                        LIVE
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-300">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-[#C9A84C]" />
                        <span>{event.event_date} • {event.event_time}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-[#C9A84C]" />
                        <span>{event.people_registered}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <Link to="/webinar" className="block pt-2">
              <Button className="w-full h-11 text-xs font-bold bg-[#0D1B2A] border border-[#C9A84C]/40 text-[#E8D4A0] hover:bg-[#C9A84C]/10 rounded-xl transition-all duration-300 flex items-center justify-center gap-2">
                Register For Live Masterclass
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Newsletter Signup */}
          <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-8 shadow-2xl backdrop-blur-xl space-y-6">
            <div className="space-y-1 border-b border-slate-700/60 pb-4">
              <h3 className="text-xl font-bold font-playfair text-white flex items-center gap-2.5">
                <Star className="h-5 w-5 text-[#C9A84C]" />
                Weekly Wealth Dispatch
              </h3>
              <p className="text-xs text-slate-300">Get tax-free growth strategies & economic updates directly</p>
            </div>

            <form onSubmit={handleSubscribe} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Enter your email address..."
                  className="w-full px-4 py-3 bg-[#0D1B2A] border border-[#C9A84C]/30 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-[#C9A84C] text-sm"
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                />
              </div>
              
              <div className="flex items-start gap-3">
                <input 
                  type="checkbox" 
                  id="newsletter-consent-resources" 
                  required
                  className="mt-1 h-4 w-4 rounded accent-[#C9A84C] bg-[#0D1B2A] border-[#C9A84C]/40"
                />
                <label htmlFor="newsletter-consent-resources" className="text-xs text-slate-300 leading-relaxed">
                  I consent to receiving financial newsletters and masterclass updates. Unsubscribe anytime. {" "}
                  <a href="#privacy" className="text-[#C9A84C] hover:underline">Privacy Policy</a>
                </label>
              </div>
              
              <Button type="submit" disabled={submitting} className="w-full h-12 text-sm font-bold bg-gradient-gold-cta text-[#070E1B] rounded-xl shadow-lg hover:opacity-95 transition-all duration-300 flex items-center justify-center gap-2">
                {submitting ? 'Subscribing…' : 'Subscribe To Dispatch'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>

            <div className="p-3.5 bg-[#0D1B2A] border border-[#C9A84C]/20 rounded-xl flex items-center justify-between text-xs text-slate-300">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="font-semibold text-emerald-300">GDPR & CCPA Compliant</span>
              </div>
              <span>No Spam • Unsubscribe Anytime</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}